import { toast } from '@webapp/components/Notification'
import { type PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type User } from '@webapp/platform/iam/entities/User'
import messages from './messages'
import { useMutateGrantReportAccessToAllSecondaryCategories } from '../../models/useMutateGrantReportAccessToAllSecondaryCategories'

export const useGrantReportAccessToAllSecondaryCategoriesPresenter = () => {
  const intl = useProcurifyIntl()
  const { grantReportAccessToAllSecondaryCategories, isGranting } =
    useMutateGrantReportAccessToAllSecondaryCategories()

  /**
   * Bulk grant across every SecondaryCategory in the PrimaryCategory
   * (BHV-021). Takes entities for the toast copy, sends only ids. Success
   * toast keyed off the resolved promise — the endpoint returns an empty 201
   * (BHV-022). Rejects after toasting so the Feature Set can run its
   * reset-to-first flow only on success.
   */
  const grantToAllSecondaryCategories = async (
    primaryCategory: PrimaryCategory,
    user: User
  ): Promise<void> => {
    try {
      await grantReportAccessToAllSecondaryCategories({
        primaryCategoryId: primaryCategory.id,
        userAuthId: user.authId,
      })

      toast.success({
        title: intl.formatMessage(messages.successToastTitle),
        children: intl.formatMessage(messages.successToastMessage, {
          userFullName: user.fullName,
          primaryCategoryName: primaryCategory.name,
        }),
      })
    } catch (err) {
      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children:
          getMessageFromError(err) ??
          intl.formatMessage(messages.errorToastDefaultMessage),
      })

      throw err
    }
  }

  return {
    commands: {
      grantToAllSecondaryCategories,
    },
    states: {
      isGranting,
    },
  }
}
